import { errors as _errors } from 'lib_ui-primitives';
import { constants, uiRuntimeConfig } from 'lib_ui-services';
const { useCaseIds } = constants;
import lodash from 'lodash';
import setDefaultValuesIfNecessary from '../../../../utilities/setDefaultValuesIfNecessary';
const { cloneDeep } = lodash;

const _p = {
    uiRuntimeConfig,
    setDefaultValuesIfNecessary
};
export const _private = _p;
export default {
    verb: 'doingValidate',
    namespace: 'deploy',
    relation: 'deployment',
    description: 'Ensure the given record has valid data',
    useCaseIds: [useCaseIds.ONE_TOUCH],
    //this is the actual logic:
    logic: validation
};

async function validation({ data, context, dispatch }) {
    const { newRecord: _newRecord } = data;
    const newRecord = cloneDeep(_newRecord);

    if (['new'].includes(newRecord.status) && !context.isNew) {
        throw new _errors.ValidationError(
            'You cannot update a deployment that is new.  Wait for it to reach a "Ready For Confirmation" status.',
            {}
        );
    }
    if (['failureNoRollback'].includes(newRecord.status)) {
        throw new _errors.ValidationError(
            'You cannot update a deployment that has failed.  Create a new deployment or copy this one.',
            {}
        );
    }

    // Need to merge in defaults to avoid validation errors for missing required values
    // that are defaulted.
    await _p.setDefaultValuesIfNecessary(context, newRecord);
    // Except for Minikube, require database credentials when the selected
    // Deployment Target differs from the current host, any credential is
    // supplied, or the user explicitly overrides target selection.
    if (
        !isMinikube(newRecord) &&
        (!isDeployingToCurrentEnvironment(newRecord) ||
            isAnyPasswordIncluded(newRecord) ||
            isTargetOverride(newRecord))
    ) {
        if (
            typeof newRecord.databasePasswordConfirmation === 'undefined' &&
            newRecord.status === 'Ready For Confirmation'
        ) {
            throw new _errors.ValidationError(
                'If you are deploying to an environment other than the current environment or you are setting a password, you must include the database password with the deployment.',
                {}
            );
        }
        if (typeof newRecord.databasePassword === 'undefined' && newRecord.status === 'new') {
            throw new _errors.ValidationError(
                'If you are deploying to an environment other than the current environment or you are setting a password, you must include the database password with the deployment.',
                {}
            );
        }
    }
    if (isTargetOverride(newRecord) && newRecord.status === 'Ready For Confirmation') {
        const userConfirmed = await confirmTargetOverride(newRecord, dispatch);
        if (!userConfirmed) {
            throw new _errors.ValidationError('Deployment target override was aborted.', {});
        }
    }
    // Normal deployment validation plans the candidate route in the deployment
    // silo. Only an HTTPRoute record at confirmation moves public traffic.
    if (newRecord['deploy:type']?.title === 'HTTPRoute' && newRecord.status === 'Ready For Confirmation') {
        const userConfirmed = await confirmPublicRouteSwitch(newRecord, dispatch);
        if (!userConfirmed) {
            throw new _errors.ValidationError('Public route switch was aborted.', {});
        }
    }
}

/**
 * Warns before an emergency deployment explicitly selects a Deployment Target that
 * may currently receive public traffic.
 *
 * @param {object} data - Deployment record from the edit form.
 * @param {Function} dispatch - UI dispatcher used to show the confirmation.
 * @returns {Promise<boolean>} Whether the user chose to continue.
 */
async function confirmTargetOverride(data, dispatch) {
    const requestedEnvironmentTitle = data['deploy:requestedEnvironment']?.title || 'the selected Environment';
    const targetTitle = data['deploy:environment']?.title || 'the selected deployment target';
    return new Promise(resolve => {
        dispatch(
            {
                message: [
                    `This will deploy to ${targetTitle} even if it is receiving public traffic for Environment ${requestedEnvironmentTitle}.`,
                    'Use this only for an emergency deployment.',
                    'Continue?'
                ],
                okButtonText: 'YES',
                icon: 'warning',
                iconColorStyle: constants.COLOR_STYLES.Warning,
                variant: constants.VARIANT.Warning,
                okAction: () => resolve(true),
                cancelAction: () => resolve(false)
            },
            { verb: 'confirm', namespace: 'application', relation: 'user' }
        );
    });
}

function isAnyPasswordIncluded(newRecord) {
    return newRecord.databasePassword || newRecord.rabbitmqErlangCookie || newRecord.rabbitmqPassword;
}

function isTargetOverride(newRecord) {
    // New records persist the explicit override decision. Established records
    // may lack that flag, so compare the logical Environment and concrete target.
    if (typeof newRecord.targetOverride === 'boolean') {
        return newRecord.targetOverride;
    }
    const requestedEnvironmentId = newRecord['deploy:requestedEnvironment']?._id;
    const targetId = newRecord['deploy:environment']?._id;
    return Boolean(
        requestedEnvironmentId && targetId && String(requestedEnvironmentId) !== String(targetId)
    );
}

function isMinikube(newRecord) {
    const targetTitle = getSelectedTargetTitle(newRecord);
    return targetTitle === 'minikube';
}
function isDeployingToCurrentEnvironment(newRecord) {
    const targetTitle = getSelectedTargetTitle(newRecord);
    if (!targetTitle) return false;
    const subdomain = _p.uiRuntimeConfig.getHostname().split('.')[0];
    return removePortFromHostname(targetTitle) === removePortFromHostname(subdomain);
}
/**
 * Returns the concrete Deployment Target title after selection. Before target
 * selection, the requested logical Environment keeps Minikube and current-host
 * validation meaningful.
 *
 * @param {object} newRecord
 * @returns {string | undefined}
 */
function getSelectedTargetTitle(newRecord) {
    return newRecord['deploy:environment']?.title || newRecord['deploy:requestedEnvironment']?.title;
}
function removePortFromHostname(hostname) {
    return hostname.split(':')[0];
}

/**
 * Asks the user to confirm the final public traffic switch.
 *
 * The persisted deploy:environment value is the concrete Deployment Target,
 * such as `staging-target-02`. Target titles encode the logical Environment as
 * their first hyphen-delimited segment, which this confirmation uses to build
 * the shared public URL. This dispatches the modal and resolves through its OK
 * or cancel action; it does not move traffic.
 *
 * @param {object} data - Deployment record from the edit form.
 * @param {Function} dispatch - UI dispatcher used to show the confirmation.
 * @returns {Promise<boolean>} Whether the user chose to continue.
 */
async function confirmPublicRouteSwitch(data, dispatch) {
    const targetTitle = data['deploy:environment'].title;
    // The deployment target title includes the target cluster suffix, such as
    // `staging-target-02`. The public hostname is based on the shared
    // Environment name, such as `staging.sstid.com`.
    const hostname = targetTitle.split('-')[0];
    const url = `https://${hostname}.sstid.com`;
    return new Promise(resolve => {
        const message = [
            `This will move public traffic for ${url} to deployment target ${targetTitle}.`,
            'Only continue after the candidate route has been tested and approved.',
            'Continue?'
        ];
        dispatch(
            {
                message,
                okButtonText: 'YES',
                icon: 'warning',
                iconColorStyle: constants.COLOR_STYLES.Warning,
                variant: constants.VARIANT.Warning,
                okAction: () => resolve(true),
                cancelAction: () => resolve(false)
            },
            { verb: 'confirm', namespace: 'application', relation: 'user' }
        );
    });
}
